<template>
  <div id="app">
    <div
      class="text-xl font-bold text-center text-primary-600 mt-4 pt-2 pb-2 mb-4 border-4 border-primary-500 rounded-full mr-20 ml-20"
    >
      Screen Time
    </div>
    <Panel pt:root="!ml-2 !mr-2" header="Kids">
      <div :class="`flex flex-col gap-2`">
        <ScreenTimeLine v-for="child in children" :key="child.id" :child="child" />
      </div>
    </Panel>
    <i
      class="pi pi-cog absolute top-2 right-4 text-gray-500 cursor-pointer"
      @click="showConfig = !showConfig"
    />
    <Panel header="Setup" pt:root="!m-2" v-if="showConfig">
      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col items-center">
          <label for="reset-kid" class="text-sm text-gray-500">Reset kid's time</label>
          <Select
            id="reset-kid"
            v-model="selectedResetChild"
            :options="children"
            optionLabel="name"
            placeholder="Select a child"
            pt:root="!m-2 !rounded-full !h-10"
            fluid
            @update:modelValue="() => resetChild(selectedResetChild)"
          />
        </div>

        <div class="flex flex-col items-center">
          <label for="reset-all" class="text-sm text-gray-500">Reset all kids' time</label>
          <Button
            id="reset-all"
            label="Reset All Kids"
            icon="pi pi-refresh"
            @click="() => resetAllChildren()"
            pt:root="!m-2 !rounded-full !h-10"
            fluid
          />
        </div>
        <label for="new-screen-time" class="text-sm text-gray-500 col-span-2 pb-2"
          >Reset screen time to {{ newScreenTime }}mins</label
        >
        <Slider
          id="new-screen-time"
          v-model="newScreenTime"
          :step="15"
          :max="120"
          :min="-120"
          :class="`col-span-2`"
        />
        <Divider class="col-span-2">
          <div
            class="text-sm text-gray-500 col-span-2 gap-2 flex flex-row items-center cursor-pointer border rounded-full p-1 pl-2 pr-2 border-gray-300"
            @click="showAddDelete = !showAddDelete"
          >
            <div>Add or delete kids</div>
            <i class="pi pi-users text-gray-500" />
          </div>
        </Divider>

        <div class="flex flex-col items-center" v-if="showAddDelete">
          <label for="add-kid" class="text-sm text-gray-500">Add a new kid</label>
          <Button
            id="add-kid"
            label="Add Kid"
            icon="pi pi-plus"
            @click="onAddChild"
            fluid
            pt:root="!m-2 !rounded-full !h-10"
          />
        </div>
        <div class="flex flex-col items-center" v-if="showAddDelete">
          <label for="delete-kid" class="text-sm text-gray-500">Delete a kid</label>
          <Select
            id="delete-kid"
            v-model="selectedChild"
            :options="children"
            optionLabel="name"
            placeholder="Select a child"
            pt:root="!m-2 !rounded-full !h-10"
            fluid
            @update:modelValue="deleteChild"
          />
        </div>
        <div class="flex flex-col items-center col-span-2" v-if="showAddDelete">
          <label for="drop-repositories" class="text-sm text-gray-500">Complete Reset</label>
          <Button
            id="drop-repositories"
            label="Reset"
            icon="pi pi-trash"
            @click="dropAllRepositories"
            severity="danger"
            pt:root="!m-2 !rounded-full !h-10"
            fluid
          />
        </div>
      </div>
    </Panel>
    <ConfirmDialog />
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import ScreenTimeLine from './components/ScreenTimeLine.vue'
  import { useRepo } from 'pinia-orm'
  import Child from './store/models/Child'
  import Button from 'primevue/button'
  import Panel from 'primevue/panel'
  import ConfirmDialog from 'primevue/confirmdialog'
  import { useConfirm } from 'primevue/useconfirm'
  import Select from 'primevue/select'
  import Divider from 'primevue/divider'
  import Slider from 'primevue/slider'
  import { useScreenTime } from './components/composables/useScreenTime'
  import ScreenTimeEvent from './store/models/ScreenTimeEvent'
  import ImpactReason from './store/models/ImpactReason'

  const showConfig = ref(false)
  const showAddDelete = ref(false)
  const selectedChild = ref(null)
  const selectedResetChild = ref(null)
  const newScreenTime = ref(60)
  const confirm = useConfirm()

  const children = computed(() => {
    // get kids from pInia ORM store
    try {
      const repo = useRepo(Child)
      const children = repo.with('screenTimeEvents').get()
      console.log('Children:', children)
      return children
    } catch (error) {
      console.error('Error fetching kids:', error)
      return []
    }
  })

  const onAddChild = () => {
    // Add a new kid to the store
    const repo = useRepo(Child)
    const newKid = new Child({ name: 'New Kid' })
    repo.save(newKid)
  }

  const deleteChild = (child) => {
    // Delete a kid from the store
    if (!child) {
      return
    }

    confirm.require({
      message: 'Are you sure you want to delete this child?',
      header: 'Delete Child',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        const repo = useRepo(Child)
        repo.destroy(child.id)
      },
    })
  }

  const resetChild = (child) => {
    // Reset a kid's screen time
    if (!child) {
      return
    }
    confirm.require({
      message: `Are you sure you want to reset ${child.name}'s screen time to ${newScreenTime.value}mins?`,
      header: 'Reset Screen Time',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        resetChildInner(child)
      },
    })
  }

  const resetChildInner = (child) => {
    // Reset a kid's screen time
    if (!child) {
      return
    }

    // Get current screen time
    const { screenTime } = useScreenTime(child)

    // Reset screen time to newScreenTime
    const adjustment = newScreenTime.value - screenTime.value
    const childId = child.id
    if (childId) {
      const repo = useRepo(ScreenTimeEvent)
      repo.save({
        childId,
        timestamp: new Date(),
        impact: adjustment,
        description: 'Reset',
        timeBefore: screenTime.value,
        timeAfter: newScreenTime.value,
      })
    }
  }

  const resetAllChildren = () => {
    // Reset all kids' screen time
    confirm.require({
      message: `Are you sure you want to reset all kids' screen time to ${newScreenTime.value}mins?`,
      header: 'Reset All Kids',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        children.value.forEach((child) => {
          resetChildInner(child)
        })
      },
    })
  }

  const dropAllRepositories = () => {
    // Drop all repositories
    confirm.require({
      message: 'Are you sure you would like to fully reset, this cannot be undone?',
      header: 'Full Reset',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        useRepo(Child).flush()
        useRepo(ScreenTimeEvent).flush()
        useRepo(ImpactReason).flush()
      },
    })
  }
</script>

<style></style>
